import type { ContentType } from "./dataTypes/ContentType";
import type { FieldDescriptor, FieldSelectOption } from "./FieldDescriptor";
import type { Field, MediaField } from "./graphFieldsExtractor";

/**
 * Advanced (presentation / runtime) field catalog — roadmap Phase 4 (§2.A/§2.B/§2.C).
 *
 * The node-form's Core fields come from two paths (the backend unified schema
 * for slopify types, the frontend legacy extractor for the rest). Advanced
 * presentation columns (font_size, color, timers, volume, looping, region
 * sizes, …) live in NEITHER source — they're non-translatable content columns
 * that the slopify schema and the PLD map don't carry. Rather than teach both
 * paths about them, we inject them from this single frontend catalog AFTER the
 * Core fields are extracted (`buildAdvancedFields` in NodeForm), so one table
 * covers every type regardless of which Core path produced its fields.
 *
 * Scalar advanced fields carry `tier: "advanced"` and render inside the
 * collapsible "Advanced" section. Advanced MEDIA fields (audio_success/fail,
 * source_video_tuto, …) render in the media strip alongside Core media — their
 * tier is documentation/ordering only (§0.4), so they're plain MediaFields.
 *
 * Numeric bounds / display modes come verbatim from Appendix C (the VR editor
 * prefab slider ranges). `displayMode: "percent"` is DISPLAY-ONLY — the raw
 * float is stored (Appendix C load-bearing note); DynamicField mirrors the %.
 */

/** Base content columns whose VR write-target is the base `contents` row
 * (§0.2 / §2.A / §2.D pt 9). On save these route to `PUT /contents/:id`, never
 * to the per-type endpoint (graphLegacy.updateContentType). */
export const BASE_CONTENT_FIELD_KEYS = [
  "time_code",
  "is_pause_scene_on_play",
  "be_scored",
] as const;

type NumberSpec = {
  kind: "number";
  key: string;
  min?: number;
  max?: number;
  displayMode?: "decimal" | "integer" | "percent";
  core?: boolean;
  helpKey?: string;
};
type BooleanSpec = { kind: "boolean"; key: string; labelKey?: string; core?: boolean };
type ColorSpec = { kind: "color"; key: string };
type MediaSpec = { kind: "media"; key: string; mediaType: MediaField["mediaType"] };
type SelectSpec = { kind: "select"; key: string; options: FieldSelectOption[]; core?: boolean };
type ArraySpec = { kind: "arrayOfObjects"; key: string };
type AdvancedSpec = NumberSpec | BooleanSpec | ColorSpec | MediaSpec | SelectSpec | ArraySpec;

const num = (
  key: string,
  min: number | undefined,
  max: number | undefined,
  displayMode: NumberSpec["displayMode"],
  core?: boolean,
): NumberSpec => ({ kind: "number", key, min, max, displayMode, core });
const bool = (key: string, labelKey?: string, core?: boolean): BooleanSpec => ({
  kind: "boolean",
  key,
  labelKey,
  core,
});
const color = (key: string): ColorSpec => ({ kind: "color", key });
const media = (key: string, mediaType: MediaField["mediaType"]): MediaSpec => ({
  kind: "media",
  key,
  mediaType,
});
const sel = (key: string, options: FieldSelectOption[], core?: boolean): SelectSpec => ({
  kind: "select",
  key,
  options,
  core,
});
const arr = (key: string): ArraySpec => ({ kind: "arrayOfObjects", key });

const BASE_UNIVERSAL: AdvancedSpec[] = [
  num("time_code", 0, undefined, "decimal"),
  bool("is_pause_scene_on_play"),
];

/** Per-type advanced specs (excluding the universal base toggles above). */
const PER_TYPE: Record<string, AdvancedSpec[]> = {
  "content-text": [
    num("font_size", 10, 60, "decimal"),
    color("color"),
    bool("has_background"),
    { ...num("timer", 0, 50, "decimal"), helpKey: "pages.project.editNode.timerInfiniteHint" },
  ],
  "content-onboarding": [
    num("font_size", 10, 100, "decimal"),
    { ...num("timer", 0, 50, "decimal"), helpKey: "pages.project.editNode.timerInfiniteHint" },
  ],
  "content-danger-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
    media("audio_success", "audio"),
    media("audio_fail", "audio"),
  ],
  "content-photo-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
  ],
  "content-thumbnail": [num("font_size", 10, 75, "decimal"), color("color")],
  "content-hotspot-scene-image": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-hotspot-scene-video": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-video-2-d": [
    color("color"),
    bool("is_looping", "is_looping_video"),
    bool("has_video_controller"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-audio": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-avatar-audio-player": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-3-d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-3d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-pdf-viewer": [num("page_to_start", 1, undefined, "integer")],
  "content-fire-extinguisher": [bool("be_scored"), media("source_video_tuto", "video")],
  "content-zone-sort": [
    num("timer", 0, 60, "decimal"),
    bool("be_scored"),
    media("source_video_tuto", "video"),
  ],
  "content-zone-detect": [num("timer", 0, 60, "decimal"), bool("be_scored")],
  "content-clean": [media("source_video_tuto", "video")],
  "content-element-catch": [
    num("game_duration", 1, 60, "integer"),
    bool("be_scored"),
    media("source_video_tuto", "video"),
  ],
  "content-random-quizz": [num("nb_questions", 1, 20, "integer"), bool("be_scored")],
  "content-speech-to-check": [
    num("respond_timer", 1, 30, "decimal"),
    num("time_begin", 0, 30, "integer"),
    bool("be_scored"),
  ],
  "content-quizz-1": [bool("be_scored")],
  "content-quizz-2": [bool("be_scored")],
  "content-quizz-3": [bool("be_scored")],
  "content-yes-no-question": [bool("be_scored")],
  "content-quizz-sort": [bool("be_scored")],
  "content-quizz-image-multiple": [bool("be_scored")],
  "content-vr-locator": [bool("be_scored")],
  "content-blur": [
    num("blur_region_width", 0.01, 7, "decimal"),
    num("blur_region_height", 0.01, 7, "decimal"),
  ],
  "content-fire": [num("fire_size", 0.01, 7, "percent")],
  "content-dust": [color("color")],
  "content-highlight": [num("content_size", 0.5, 2, "decimal")],
  "content-tuto": [bool("has_audio")],
  "content-button-1": [color("color")],
  "content-button-reload": [color("color")],
  "content-scene": [bool("is_looping"), num("sound_volume", 0, 1, "percent")],
  "content-avatar-ai-persona": [
    sel(
      "chat_service",
      [
        { value: "openai", optionText: "ChatGPT" },
        { value: "mistral", optionText: "Mistral" },
      ],
      true,
    ),
    num("max_nb_dialog", 1, 999, "integer", true),
    bool("activate_debriefing", undefined, true),
    arr("pdf_items"),
    bool("is_showing_user_text"),
    bool("is_showing_avatar_text"),
    bool("activate_auto_rec"),
    bool("is_dialog_started_by_user"),
    bool("is_silence_detection_activated"),
    num("min_silent_time_sec_before_avatar_reaction", 0, 30, "decimal"),
    num("max_silent_time_sec_before_avatar_reaction", 0, 30, "decimal"),
    bool("is_speech_interruption_activated"),
    bool("is_speech_interruption_by_user_activated"),
    num("avatar_height", 0, 2, "decimal"),
    num("avatar_debrief_height", 0, 2, "decimal"),
    bool("be_scored"),
    media("source_avatar", "avatar"),
    media("source_avatar_debrief", "avatar"),
  ],
  "content-avatar-ai": [
    sel(
      "chat_service",
      [
        { value: "openai", optionText: "ChatGPT" },
        { value: "mistral", optionText: "Mistral" },
      ],
      true,
    ),
    num("max_nb_dialog", 1, 999, "integer", true),
    bool("allow_internet_search", undefined, true),
    arr("source_medias"),
    bool("is_showing_user_text"),
    bool("is_showing_avatar_text"),
    bool("activate_auto_rec"),
    media("source_avatar", "avatar"),
  ],
};

const AVATAR_TRACK = new Set(["content-avatar-ai-session-debrief"]);

const makeDescriptor = (spec: AdvancedSpec): FieldDescriptor => {
  const isCore = "core" in spec && spec.core === true;
  const base = {
    key: spec.key,
    description: null,
    required: false,
    pldKey: null,
    isTranslatable: false,
    tier: isCore ? undefined : ("advanced" as const),
  };
  switch (spec.kind) {
    case "number":
      return {
        ...base,
        widget: "number",
        jsonSchema: {
          type: spec.displayMode === "integer" ? "integer" : "number",
          ...(spec.min !== undefined ? { minimum: spec.min } : {}),
          ...(spec.max !== undefined ? { maximum: spec.max } : {}),
        },
        displayMode: spec.displayMode ?? "decimal",
        ...(spec.helpKey ? { helpKey: spec.helpKey } : {}),
      };
    case "boolean":
      return {
        ...base,
        widget: "boolean",
        jsonSchema: { type: "boolean" },
        ...(spec.labelKey ? { labelKey: spec.labelKey } : {}),
      };
    case "color":
      return { ...base, widget: "color", jsonSchema: { type: "string" } };
    case "select":
      return {
        ...base,
        widget: "select",
        jsonSchema: { type: "string" },
        options: spec.options,
      };
    case "arrayOfObjects":
      return { ...base, widget: "arrayOfObjects", jsonSchema: { type: "array" }, tier: undefined };
    case "media":
      return { ...base, widget: "media", jsonSchema: {}, tier: undefined };
  }
};

const BASE_CONTENT_FIELD_KEY_SET = new Set<string>(BASE_CONTENT_FIELD_KEYS);

const specToField = (
  spec: AdvancedSpec,
  contentObject: Record<string, unknown>,
  baseContentObject?: Record<string, unknown>,
): Field => {
  const descriptor = makeDescriptor(spec);
  const currentValue =
    BASE_CONTENT_FIELD_KEY_SET.has(spec.key) && baseContentObject != null
      ? baseContentObject[spec.key] ?? contentObject?.[spec.key]
      : contentObject?.[spec.key];
  switch (spec.kind) {
    case "media":
      return {
        type: "media",
        mediaType: spec.mediaType,
        fieldName: spec.key,
        currentValue: currentValue as MediaField["currentValue"],
        descriptor,
      };
    case "boolean":
      return { type: "boolean", fieldName: spec.key, currentValue: !!currentValue, descriptor };
    case "number":
      return {
        type: "number",
        fieldName: spec.key,
        currentValue: currentValue as number | string | undefined,
        descriptor,
      };
    case "color":
      return {
        type: "color",
        fieldName: spec.key,
        currentValue: currentValue as string | undefined,
        descriptor,
      };
    case "select": {
      const isStringifiablePrimitive =
        typeof currentValue === "string" ||
        typeof currentValue === "number" ||
        typeof currentValue === "boolean";
      return {
        type: "select",
        fieldName: spec.key,
        currentValue: isStringifiablePrimitive ? String(currentValue) : "",
        descriptor,
      };
    }
    case "arrayOfObjects": {
      let arrValue: unknown[] = [];
      if (Array.isArray(currentValue)) arrValue = currentValue;
      else if (typeof currentValue === "string") {
        try {
          const parsed: unknown = JSON.parse(currentValue);
          if (Array.isArray(parsed)) arrValue = parsed;
        } catch {
          arrValue = [];
        }
      }
      if (spec.key === "source_medias") {
        arrValue = arrValue.map((e) =>
          e && typeof e === "object" ? Number((e as { id?: unknown }).id) : Number(e),
        );
      }
      return {
        type: "arrayOfObjects",
        fieldName: spec.key,
        currentValue: arrValue,
        descriptor,
      };
    }
  }
};

/**
 * Build the advanced Field[] for a content node, reading current values off the
 * content object. Excludes the avatar track. Callers dedup against the Core
 * fields already extracted (a spec whose key a Core field already covers is
 * dropped so we never render a column twice).
 */
export const buildAdvancedFields = (
  contentType: ContentType,
  baseContentObject?: Record<string, unknown>,
): Field[] => {
  const modelName = contentType.modelName;
  if (AVATAR_TRACK.has(modelName)) return [];
  const specs =
    modelName === "content-scene"
      ? PER_TYPE[modelName] ?? []
      : [...BASE_UNIVERSAL, ...(PER_TYPE[modelName] ?? [])];
  const contentObject = (contentType.object as unknown as Record<string, unknown>) ?? {};
  const seen = new Set<string>();
  const fields: Field[] = [];
  for (const spec of specs) {
    if (seen.has(spec.key)) continue;
    seen.add(spec.key);
    fields.push(specToField(spec, contentObject, baseContentObject));
  }
  return fields;
};

export const isAdvancedField = (field: Field): boolean => field.descriptor?.tier === "advanced";

/**
 * Consistent internal ordering of the Advanced section (Sébastien 2026-07-16):
 * every advanced field belongs to one of two groups, rendered presentation
 * first, then configuration, separated by an unlabeled hairline. The split is
 * SEMANTIC, not per-type — a key always lands in the same group everywhere:
 *   - "presentation" — what the learner sees/hears: sizes, colors, animation,
 *     media playback (loop/volume/spatialization), display toggles.
 *   - "config" — how the interaction behaves: triggers/timing (time_code,
 *     timers), scoring, gameplay rules, dialog-flow switches.
 * Debatable calls, settled once here: is_hidden_at_start + timer govern WHEN
 * the content appears/disappears → config; is_looping/volume govern HOW the
 * media renders → presentation.
 */
export const ADVANCED_FIELD_GROUPS: Record<string, "presentation" | "config"> = {
  font_size: "presentation",
  color: "presentation",
  has_background: "presentation",
  has_activate_animation: "presentation",
  is_looping: "presentation",
  has_video_controller: "presentation",
  sound_volume: "presentation",
  sound_distance: "presentation",
  is_spatialized: "presentation",
  height: "presentation",
  is_self_rotating: "presentation",
  content_size: "presentation",
  fire_size: "presentation",
  blur_region_width: "presentation",
  blur_region_height: "presentation",
  avatar_height: "presentation",
  avatar_debrief_height: "presentation",
  is_showing_user_text: "presentation",
  is_showing_avatar_text: "presentation",
  time_code: "config",
  is_pause_scene_on_play: "config",
  be_scored: "config",
  is_hidden_at_start: "config",
  timer: "config",
  catch_time: "config",
  game_duration: "config",
  nb_questions: "config",
  respond_timer: "config",
  time_begin: "config",
  page_to_start: "config",
  has_audio: "config",
  activate_auto_rec: "config",
  is_dialog_started_by_user: "config",
  is_silence_detection_activated: "config",
  min_silent_time_sec_before_avatar_reaction: "config",
  max_silent_time_sec_before_avatar_reaction: "config",
  is_speech_interruption_activated: "config",
  is_speech_interruption_by_user_activated: "config",
};

/** Unknown keys default to "config" — but the catalog test enforces that every
 * advanced spec is EXPLICITLY classified, so the default only covers drift. */
export const advancedFieldGroup = (fieldKey: string): "presentation" | "config" =>
  ADVANCED_FIELD_GROUPS[fieldKey] ?? "config";

/** Model names carrying a PER_TYPE entry — exported for the classification
 * coverage test (every advanced scalar must be explicitly grouped). */
export const CATALOGED_MODEL_NAMES: readonly string[] = Object.keys(PER_TYPE);
